import axios, { AxiosInstance } from 'axios'
import { REACT_APP_BASE_URL } from '../components/utils/constant'
import { BaseHTTP } from './base.http'
import {
  IBusiness,
  ICreateBusiness,
  ICreateRepresentanteLegal,
  BusinessDetails,
  IGetDocumentsTypesRequest,
  IGetListSummaryMovementsSpecificRequest
} from '../models/Business'

export enum TypesLibro {
  COMPRA = 'compra',
  VENTA = 'venta',
  HONORARIO = 'honorario',
  TRANSACCION = 'transaccion',
  MANUAL = 'manual',
  PROTOCOMPRA = 'compra-manual',
  PROTOVENTA = 'venta-manual',
  PROTOCOMPRAPRESTAMO = 'compra-manual-prestamo',
  PROTOVENTAPRESTAMO = 'venta-manual-prestamo',
  VINCULOS = 'vinculos',
  REMUNERACION = 'remuneracion',
  DOC29 = 'doc-29',
  DELETE = 'delete'
}

type DocumentPdfUrlParams = {
  businessId: number
  query: string
  folio: string | number
  rut?: string
}

export class BusinessAPI {
  private http!: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  getListBusiness() {
    const path = 'v1/business'
    return this.http.get<IBusiness[]>(path)
  }

  recordAccess(businessId: number) {
    const path = `v1/business/${businessId}/access`
    return this.http.post<void>(path)
  }

  getBusinessById(businessId: number) {
    const path = `v1/business/${businessId}`
    return this.http.get<BusinessDetails>(path)
  }

  getFirstLast(businessId: number, type: TypesLibro) {
    const path = `v1/business/${businessId}/first-last`
    return this.http.get(path, { params: { type } })
  }

  // getBusinessById(businessId: number) {
  //   const path = `v1/business/${businessId}`
  //   return this.http.get<IBusiness>(path)
  // }

  createBusiness(business: ICreateBusiness, businessId?: number) {
    const path = 'v1/business'
    const params = businessId ? { business: businessId } : undefined
    return this.http.post<IBusiness>(path, business, { params })
  }

  updateBusinessById(businessId: number, business: IBusiness) {
    const path = `v1/business/${businessId}`
    return this.http.patch<IBusiness>(path, business)
  }

  updateImageByBusinessId(imgFile: File, bussinessID: number) {
    const formData = new FormData()
    formData.append('file', imgFile)
    const path = `v1/business/${bussinessID}/image`
    return this.http.patch<IBusiness>(path, formData)
  }

  getListSummaryMovements(params) {
    const { businessId, type, month, year, linkage, limit, ...filters } = params
    const path = `v1/business/${businessId}/summary-movements`
    const queryParams = {
      type,
      month,
      year,
      max: limit || 10000,
      ...filters
    }
    if (linkage === undefined) {
      queryParams.linkage = linkage
    }
    return this.http.get(path, { params: queryParams })
  }

  getListSummaryVinculos(params, requestOptions = {}) {
    const { businessId, month, year, linkage, limit, ...filters } = params
    const path = `v1/business/${businessId}/summary-vinculos`
    const queryParams = {
      month,
      year,
      max: limit || 10000,
      ...filters
    }
    if (linkage === undefined) {
      queryParams.linkage = linkage
    }
    return this.http.get(path, { ...requestOptions, params: queryParams })
  }

  getLinkageTypes(params: { businessId: number; year: number; month?: number }) {
    const { businessId, year, month = 0 } = params
    const path = `v1/business/${businessId}/linkage-types`
    return this.http.get(path, { params: { year, month } })
  }

  getListSummaryMovementsSpecific(params: IGetListSummaryMovementsSpecificRequest) {
    const {
      businessId,
      type,
      month,
      year,
      linkage,
      max,
      page,
      searchQuery,
      documentStatus,
      minAmount,
      maxAmount,
      code,
      aprobadoMin,
      folio,
      nombreCuenta,
      centroCosto
    } = params
    const path = `v1/business/${businessId}/summary-movements-specific`
    const queryParams = {
      type,
      month,
      year,
      max,
      page,
      searchQuery,
      documentStatus,
      linkage,
      minAmount,
      maxAmount,
      code,
      aprobadoMin,
      folio,
      nombreCuenta,
      centroCosto
    } as Record<string, unknown>
    return this.http.get(path, { params: queryParams })
  }

  getSummaryMovementById(businessId: number, movementId: number) {
    const path = `v1/business/${businessId}/summary-movements/${movementId}`
    return this.http.get(path)
  }

  getVentaDocumentPdfUrl(params: DocumentPdfUrlParams) {
    const { businessId, query, folio, rut } = params
    const path = `v1/business/${businessId}/venta-pdf-url`
    return this.http.get(path, { params: { query, folio, rut } })
  }

  getVentaDocumentPdfUrlsSummary(params: { businessId: number; queries: string[] }) {
    const { businessId, queries } = params
    const path = `v1/business/${businessId}/venta-pdf-urls-summary`
    return this.http.get<Record<string, boolean>>(path, { params: { queries: queries.join(',') } })
  }

  getCompraDocumentPdfUrl(params: DocumentPdfUrlParams) {
    const { businessId, query, folio, rut } = params
    const path = `v1/business/${businessId}/compra-pdf-url`
    return this.http.get(path, { params: { query, folio, rut } })
  }

  getCompraDocumentPdfUrlsSummary(params: { businessId: number; queries: string[] }) {
    const { businessId, queries } = params
    const path = `v1/business/${businessId}/compra-pdf-urls-summary`
    return this.http.get<Record<string, boolean>>(path, { params: { queries: queries.join(',') } })
  }

  getHonorarioDocumentPdfUrl(params: { businessId: number; query: string; folio: string | number }) {
    const { businessId, query, folio } = params
    const path = `v1/business/${businessId}/honorario-pdf-url`
    return this.http.get(path, { params: { query, folio } })
  }

  getHonorarioDocumentPdfUrlsSummary(params: { businessId: number; queries: string[] }) {
    const { businessId, queries } = params
    const path = `v1/business/${businessId}/honorario-pdf-urls-summary`
    return this.http.get<Record<string, boolean>>(path, { params: { queries: queries.join(',') } })
  }

  getPorCobrarCobradas(params) {
    const { businessId, limit, type, state, order } = params
    const path = `v1/business/${businessId}/multi-boleta-factura?limit=${limit}&type=${type}&state=${state}&order=${order}`
    return this.http.get(path)
  }

  getPorPagarPagadas(params) {
    const { businessId, limit, type, state, order } = params
    const path = `v1/business/${businessId}/multi-boleta-factura?limit=${limit}&type=${type}&state=${state}&order=${order}`
    return this.http.get(path)
  }

  getTopFive(params) {
    const { businessId, type } = params
    const path = `v1/business/${businessId}/rankings-${type}?code=1M&limit=5`
    return this.http.get(path)
  }

  getHistoricalVsCurrentIncome(params) {
    const { businessId, period } = params
    const path = `v1/business/${businessId}/ventas-historicas?limit=5&code=${period}`
    return this.http.get(path)
  }

  getRankingEgresos(params) {
    const { businessId } = params
    const path = `v1/business/${businessId}/ranking-cuenta?code=1M&limit=5`
    return this.http.get(path)
  }

  postBusinessDashboard(payload) {
    const path = 'v1/business'
    return this.http.post(path, payload)
  }

  getDataLibroDiario(params) {
    const { businessId, ...queryParams } = params
    const path = `v1/business/${businessId}/libro-diario`
    return this.http.get(path, { params: queryParams })
  }

  getCuentasUsadas(params) {
    const { businessId } = params
    const path = `v1/cuenta-contable/business/${businessId}/cuentas-last-level`
    return this.http.get(path)
  }

  getCuentasUsadasLibroMayor(params) {
    const { businessId } = params
    const path = `v1/business/${businessId}/cuentas-utilizadas`
    return this.http.get(path)
  }

  getDataLibroMayor(params) {
    const { businessId, idCuenta, dateFrom, dateTo, limit, page } = params
    console.log('params', params)
    const queryParams = {
      fromYear: dateFrom.year,
      toYear: dateTo.year,
      fromMonth: dateFrom.month,
      toMonth: dateTo.month,
      ...(limit && { limit }),
      ...(page !== undefined && { page })
    }
    const path = `v1/business/${businessId}/libro-mayor/cuenta/${idCuenta}`
    return this.http.get(path, { params: queryParams })
  }

  createRepresentanteLegal(businessId: number, payload: ICreateRepresentanteLegal) {
    const path = `v1/business/${businessId}/representante`
    return this.http.patch<IBusiness>(path, payload)
  }

  getLibroClienteProveedor(params) {
    const { businessId, type, from, to, max } = params
    const path = `v1/business/${businessId}/libro-cliente-proveedor?type=${type}&from=${from}&to=${to}&max=${max}`
    return this.http.get(path)
  }

  getEstadoResultado(params) {
    const { businessId, from, to } = params
    const path = `v1/business/${businessId}/estado-resultado?from=${from}&to=${to}`
    return this.http.get(path)
  }

  getEstadoResultadoExtend(params: {
    businessId: number
    from?: string
    to?: string
    centroCostoId?: number
    sinAsignar?: boolean
  }) {
    const { businessId, from, to, centroCostoId, sinAsignar } = params
    const path = `v1/business/${businessId}/estado-resultado-extend`
    const queryParams: Record<string, unknown> = {}
    if (from) {
      queryParams.from = from
    }
    if (to) {
      queryParams.to = to
    }
    if (centroCostoId !== undefined && centroCostoId !== null) {
      queryParams.centroCostoId = centroCostoId
    }
    if (sinAsignar) {
      queryParams.sinAsignar = true
    }
    return this.http.get(path, { params: queryParams })
  }

  getResumenF29(params: { businessId: number; month?: string; page?: number; pageSize?: number }) {
    const { businessId, month, page = 0, pageSize = 1000 } = params
    const path = `v1/libro-contable/business/${businessId}/formulario-impuestos`
    const queryParams: Record<string, unknown> = {
      page,
      pageSize
    }
    if (month) {
      queryParams.month = month
    }

    return this.http.get(path, { params: queryParams })
  }

  exportEstadoResultado(params: { businessId: number; email: string; from: string; to: string }) {
    const { businessId, email, from, to } = params
    const path = `v1/business/${businessId}/estado-resultado/export`
    const payload = { email, from, to }
    return this.http.post(path, payload)
  }

  exportEstadoResultadoExtend(params: {
    businessId: number
    email: string
    from?: string
    to?: string
    centroCostoId?: number
    sinAsignar?: boolean
  }) {
    const { businessId, email, from, to, centroCostoId, sinAsignar } = params
    const path = `v1/business/${businessId}/estado-resultado-extend/export`
    const payload: Record<string, unknown> = { email }
    if (from) payload.from = from
    if (to) payload.to = to
    if (centroCostoId !== undefined && centroCostoId !== null) {
      payload.centroCostoId = centroCostoId
    }
    if (sinAsignar) {
      payload.sinAsignar = true
    }
    return this.http.post(path, payload)
  }

  exportBalanceGeneral(params: { businessId: number; email: string; from?: string; to?: string }) {
    const { businessId, email, from, to } = params
    const path = `v1/business/${businessId}/balance/export`
    const payload = {
      email,
      ...(from && { from }),
      ...(to && { to })
    }
    return this.http.post(path, payload)
  }

  getTransacciones(params) {
    const { businessId, type } = params
    const path = `v1/business/${businessId}/transaction-list?type=${type}&monto=${1}`
    return this.http.get(path)
  }

  postPagoArriendo(businessId, payload, model = 'pago-arriendo') {
    const path = `v1/business/${businessId}/libros-pre-model?model=${model}`
    return this.http.post(path, payload)
  }

  uploadModelLibroExcel(businessId: number, payload: FormData, model: string) {
    const path = `v1/business/${businessId}/model-libro-excel?model=${encodeURIComponent(model)}`
    return this.http.post(path, payload)
  }

  postAnterior(businessId, payload, model = 'pago-anterior') {
    const path = `v1/business/${businessId}/libros-pre-model?model=${model}`
    return this.http.post(path, payload)
  }

  getCobradoBusiness(businessId) {
    const path = `v1/business/${businessId}/diferencia-cobro`
    return this.http.get(path)
  }

  getResumenCobrosYPagos(params: {
    businessId: number
    startDate: string
    endDate: string
  }): Promise<[]> {
    const { businessId, startDate, endDate } = params
    const path = `v1/business/${businessId}/resumen-cobros-pagos?startDate=${startDate}&endDate=${endDate}`
    return this.http.get(path)
  }

  getListWorker(params) {
    const { businessId, queryFrom, queryTo } = params
    const path = `/v1/remuneration/business/${businessId}/list-workers?queryFrom=${queryFrom}&queryTo=${queryTo}`
    return this.http.get(path)
  }

  postUpload(businessId, payload, headers = {}) {
    const path = `v1/remuneration/business/${businessId}/upload`
    return BaseHTTP.getInstance().http.post(path, payload, headers)
  }

  postBukUploadExcel(businessId: number, payload: FormData, date: string) {
    const path = `v1/remuneration/business/${businessId}/buk-upload-excel`
    return this.http.post(path, payload, { params: { date } })
  }

  downloadRemunerationTemplate(templateType: 'buk' | 'remuneracion' | 'rendicion-gasto' | 'plantillas-prestamo' | 'plantillas-invoice') {
    const path = `v1/remuneration/plantilla/${templateType}`
    return this.http.get(path, { responseType: 'blob' })
  }

  uploadRendicionGastosExcel(businessId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    const path = `v1/business/${businessId}/rendicion-gastos-excel`
    return this.http.post(path, formData)
  }

  postIndividual(businessId, payload) {
    const path = `v1/remuneration/business/${businessId}/insert`
    return this.http.post(path, payload)
  }

  getListSummaryMovementsFromTo(params: {
    businessId: number // equivale a {id} en la ruta del gateway
    linkage?: number | boolean
    from: string // “YYYY-MM-DD”
    to: string // “YYYY-MM-DD”
    limit?: number
    page?: number
    status?: string
    accountId?: number
    type?: string
  }) {
    const { businessId, linkage, from, to, limit, page, status, accountId, type } = params
    const path = `/v1/business/${businessId}/lista-summary-from-to`
    const queryParams = {
      from,
      to,
      limit: limit || 10000,
      page,
      status,
      accountId,
      type
    } as Record<string, unknown>
    if (linkage === undefined) {
      queryParams.linkage = linkage
    }
    return this.http.get(path, { params: queryParams })
  }

  getDocumentsTypes(params: IGetDocumentsTypesRequest) {
    const { businessId, year, month, type } = params
    const path = `v1/business/${businessId}/documents-types`
    return this.http.get(path, { params: { year, month, type } })
  }

  getAccountsTypes(params: IGetDocumentsTypesRequest) {
    const { businessId, year, month, type } = params
    const path = `v1/business/${businessId}/accounts-types`
    return this.http.get(path, { params: { year, month, type } })
  }

  getLatestVisionDelNegocio(params) {
    const { businessId } = params
    const path = `v1/business/${businessId}/vision-del-negocio/latest`
    return this.http.get(path)
  }

  getVisionDelNegocio(params) {
    const { businessId } = params
    const path = `v1/business/${businessId}/vision-del-negocio`
    return this.http.get(path)
  }

  exportSummaryMovements(params: {
    organizationId: number
    businessId: number
    email: string
    type: string
    month: number
    year: number
    code?: number
    linkage?: boolean
    searchQuery?: string
    documentStatus?: string
  }) {
    const { businessId, email, type, month, year, code, linkage, searchQuery, documentStatus } =
      params
    const path = `v1/business/${businessId}/summary-movements/export`
    const payload = {
      email,
      type,
      month,
      year,
      ...(code && { code }),
      ...(linkage !== undefined && { linkage }),
      ...(searchQuery && { searchQuery }),
      ...(documentStatus && { documentStatus })
    }
    return this.http.post(path, payload)
  }

  exportMovementsTransactions(params: {
    organizationId: number
    businessId: number
    productId: number
    email: string
    year: number
    month?: number
    searchQuery?: string
    account?: string
    transactionType?: string
  }) {
    const { businessId, productId, email, year, month, searchQuery, account, transactionType } =
      params
    const path = `v1/business/${businessId}/producto-bancario/${productId}/summary-movements/export`
    const payload = {
      email,
      year,
      ...(month && { month }),
      ...(searchQuery && { searchQuery }),
      ...(account && { account }),
      ...(transactionType && { transactionType })
    }
    return this.http.post(path, payload)
  }

  exportBancoConciliaciones(params: {
    businessId: number
    email: string
    year: number
    month: number
  }) {
    const { businessId, email, year, month } = params
    const path = `v1/business/${businessId}/bancoconciliaciones/export`
    const payload = { email, year, month }
    return this.http.post(path, payload)
  }

  exportLibroAuxiliar(params: {
    organizationId: number
    businessId: number
    email: string
    from: string
    to: string
    linkage?: boolean
    type?: string
    documentStatus?: string
    accountId?: number
  }) {
    const { businessId, email, from, to, linkage, type, documentStatus, accountId } = params
    const path = `v1/business/${businessId}/lista-summary-from-to/libro-auxiliar/export`
    const payload = {
      from,
      to,
      email,
      ...(linkage !== undefined && { linkage }),
      ...(type && { type }),
      ...(documentStatus && { documentStatus }),
      ...(accountId && { accountId })
    }
    return this.http.post(path, payload)
  }

  exportDocumentsReport(params: {
    organizationId: number
    businessId: number
    email: string
    from: string
    to: string
    linkage?: boolean
    type?: string
    documentStatus?: string
    accountId?: number
  }) {
    const { businessId, email, from, to, linkage, type, documentStatus, accountId } = params
    const path = `v1/business/${businessId}/documents-report/export`
    const payload = {
      from,
      to,
      email,
      ...(linkage !== undefined && { linkage }),
      ...(type && { type }),
      ...(documentStatus && { documentStatus }),
      ...(accountId && { accountId })
    }
    return this.http.post(path, payload)
  }

  exportLibroDiario(params: {
    organizationId: number
    businessId: number
    email: string
    queryFrom?: string
    queryTo?: string
    searchQuery?: string
    transactionType?: string
  }) {
    const { businessId, email, queryFrom, queryTo, searchQuery, transactionType } = params
    const path = `v1/business/${businessId}/libro-diario/export`
    const payload = {
      email,
      ...(queryFrom && { queryFrom }),
      ...(queryTo && { queryTo }),
      ...(searchQuery && { searchQuery }),
      ...(transactionType && { transactionType })
    }
    return this.http.post(path, payload)
  }

  exportLibroMayor(params: {
    organizationId: number
    businessId: number
    email: string
    idCuenta?: number
    dateFrom?: { year: number; month: number }
    dateTo?: { year: number; month: number }
    searchQuery?: string
  }) {
    const { businessId, email, idCuenta, dateFrom, dateTo, searchQuery } = params
    const path = `v1/business/${businessId}/libro-mayor/cuenta/${idCuenta}/export`
    const payload = {
      email,
      fromYear: dateFrom?.year,
      fromMonth: dateFrom?.month,
      toYear: dateTo?.year,
      toMonth: dateTo?.month,
      searchQuery
    }
    return this.http.post(path, payload)
  }

  previewMercadoPagoOAuth(organizationId: number, businessId: number, code: string, identifier?: string) {
    const path = `v1/business/${businessId}/integrations/mercadopago/oauth/preview`
    return this.http.post(path, { code, organizationId, identifier })
  }

  confirmMercadoPagoOAuth(businessId: number, identifier?: string, accountName?: string, bankAccountId?: number) {
    const path = `v1/business/${businessId}/integrations/mercadopago/oauth/confirm`
    return this.http.post(path, { identifier, accountName, bankAccountId })
  }

  cancelMercadoPagoOAuth(businessId: number, identifier?: string) {
    const path = `v1/business/${businessId}/integrations/mercadopago/oauth/cancel`
    return this.http.post(path, { identifier })
  }

  handleMercadoPagoOAuthCallback(organizationId: number, businessId: number, code: string, identifier?: string) {
    const path = `v1/business/${businessId}/integrations/mercadopago/oauth/callback`
    return this.http.post(path, { code, organizationId, identifier })
  }

  connectMercadoPagoWithAccessToken(businessId: number, accessToken: string, identifier?: string) {
    const path = `v1/business/${businessId}/integrations/mercadopago/connect`
    return this.http.post(path, { accessToken, identifier })
  }

  getMercadoPagoAccounts(businessId: number) {
    const path = `v1/business/${businessId}/integrations/mercadopago/accounts`
    return this.http.get(path)
  }

  getMercadoPagoPosPayments(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
      status?: string
      externalReference?: string
      limit?: number
      skip?: number
    }
  ) {
    const queryParams = new URLSearchParams()
    queryParams.set('organizationId', String(organizationId))
    if (params.beginDate) queryParams.set('beginDate', params.beginDate)
    if (params.endDate) queryParams.set('endDate', params.endDate)
    if (params.status) queryParams.set('status', params.status)
    if (params.externalReference) queryParams.set('externalReference', params.externalReference)
    if (params.limit !== undefined) queryParams.set('limit', String(params.limit))
    if (params.skip !== undefined) queryParams.set('skip', String(params.skip))

    const path = `v1/business/${businessId}/mercadopago/pos/payments?${queryParams.toString()}`
    return this.http.get(path)
  }

  syncMercadoPagoPosPayments(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
    }
  ) {
    const path = `v1/business/${businessId}/mercadopago/pos/sync`
    return this.http.post(path, {
      beginDate: params.beginDate,
      endDate: params.endDate
    })
  }

  uploadAbonos(businessId: number, organizationId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const path = `v1/business/${businessId}/transactions/upload/abonos`
    return this.http.post(path, formData)
  }

  uploadVentas(businessId: number, organizationId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const path = `v1/business/${businessId}/transactions/upload/ventas`
    return this.http.post(path, formData)
  }

  uploadCartola(businessId: number, organizationId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const path = `v1/business/${businessId}/transactions/upload/cartola`
    return this.http.post(path, formData)
  }

  getAllTransactions(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
      source?: string
      limit?: number
      page?: number
      searchQuery?: string
    }
  ) {
    const queryParams = new URLSearchParams()
    if (params.beginDate) queryParams.append('beginDate', params.beginDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.source) queryParams.append('source', params.source)
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery)

    const path = `v1/business/${businessId}/transactions/all?${queryParams.toString()}`
    return this.http.get(path)
  }

  getDigitalPayments(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
      source?: string
      searchQuery?: string
      operationType?: string
      bankAccountId?: number
      limit?: number
      page?: number
    }
  ) {
    const queryParams = new URLSearchParams()
    if (params.beginDate) queryParams.append('beginDate', params.beginDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.source) queryParams.append('source', params.source)
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery)
    if (params.operationType) queryParams.append('operationType', params.operationType)
    if (params.bankAccountId !== undefined) queryParams.append('bankAccountId', String(params.bankAccountId))
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit))
    if (params.page !== undefined) queryParams.append('page', String(params.page))

    const path = `v1/business/${businessId}/transactions/digitalpayments?${queryParams.toString()}`
    return this.http.get(path)
  }

  getBoletasElectronicasDetalle(params: {
    businessId: number
    year?: number
    month?: number
    day?: number
    dte?: number
    limit?: number
    skip?: number
    folio?: number | string
  }) {
    const { businessId, year, month, day, dte, limit, skip, folio } = params
    const path = `v1/business/${businessId}/boletas-electronicas`
    return this.http.get<{
      documents: Record<string, unknown>[]
      total: number
      resumenDia?: { year: number; month: number; day: number; dte: number | null }
    }>(path, {
      params: { year, month, day, dte, limit, skip, folio }
    })
  }

  getDigitalPaymentsSummary(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
      source?: string
      operationType?: string
      bankAccountId?: number
      limit?: number
      page?: number
      skip?: number
    }
  ) {
    const queryParams = new URLSearchParams()
    queryParams.set('organizationId', String(organizationId))
    if (params.beginDate) queryParams.append('beginDate', params.beginDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.source) queryParams.append('source', params.source)
    if (params.operationType) queryParams.append('operationType', params.operationType)
    if (params.bankAccountId !== undefined) queryParams.append('bankAccountId', String(params.bankAccountId))
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit))
    if (params.page !== undefined) queryParams.append('page', String(params.page))
    if (params.skip !== undefined) queryParams.append('skip', String(params.skip))

    const path = `v1/business/${businessId}/transactions/digitalpayments/summary?${queryParams.toString()}`
    return this.http.get(path)
  }

  getDigitalPaymentsByDate(
    businessId: number,
    organizationId: number,
    params: {
      beginDate: string
      endDate: string
      source?: string
      searchQuery?: string
    }
  ) {
    const queryParams = new URLSearchParams()
    queryParams.set('organizationId', String(organizationId))
    queryParams.set('beginDate', params.beginDate)
    queryParams.set('endDate', params.endDate)
    if (params.source) queryParams.append('source', params.source)
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery)

    const path = `v1/business/${businessId}/transactions/digitalpayments?${queryParams.toString()}`
    return this.http.get(path)
  }

  getBankMovements(
    businessId: number,
    organizationId: number,
    params: {
      beginDate?: string
      endDate?: string
      source?: string
      limit?: number
      page?: number
      searchQuery?: string
    }
  ) {
    const queryParams = new URLSearchParams()
    if (params.beginDate) queryParams.append('beginDate', params.beginDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.source) queryParams.append('source', params.source)
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery)

    const path = `v1/business/${businessId}/transactions/bankmovements?${queryParams.toString()}`
    return this.http.get(path)
  }

  getMonthlySummary(
    businessId: number,
    params?: { year?: number; month?: number; provider?: 'mercadopago' | 'getnet' | 'transbank' }
  ) {
    const queryParams = new URLSearchParams()
    if (params?.year !== undefined) queryParams.append('year', String(params.year))
    if (params?.month !== undefined) queryParams.append('month', String(params.month))
    if (params?.provider) queryParams.append('provider', params.provider)
    const query = queryParams.toString()
    const path = `v1/business/${businessId}/transactions/monthly-summary${query ? `?${query}` : ''}`
    return this.http.get(path)
  }

  postComentarioMovimiento(businessId: number, movementId: number, comentario?: string) {
    const path = `v1/business/${businessId}/comentario/${movementId}`
    return this.http.post(path, { comentario })
  }
}

export default new BusinessAPI()

const url = REACT_APP_BASE_URL
let changeableUrl = ''

// set cuenta
export const setCuenta = async PK => {
  changeableUrl = `${url}/setCuenta/${PK}`
  return axios.get(changeableUrl)
}
